import { FormCommonProps } from "src/components/common/forms/types.ts";
import i18n from "i18next";
import { PurchaseRequestApi } from "src/api/generated/erp/purchase/purchaseRequest/purchaseRequestApi.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { IsoDateString } from "src/types/dateTime.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { useStoredState } from "src/utils/useStoredState.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PurchaseOrderFromPurchaseRequestFormProps extends FormCommonProps<void> {
	purchaseRequestIds: number[];
}

interface FieldValues {
	purchaseRequestIds: Array<number>;
	plannedArrivalDate: IsoDateString;
	sumSamePartRequests: boolean;
	tryAppendToExistingPurchaseOrderLine: boolean;
}

export const PurchaseOrderFromPurchaseRequestForm = ({
	purchaseRequestIds,
	onFormEdited,
	onCompleted,
}: PurchaseOrderFromPurchaseRequestFormProps) => {
	const [storedDefaultValues, setStoredDefaultValues] = useStoredState<
		Pick<FieldValues, "sumSamePartRequests" | "tryAppendToExistingPurchaseOrderLine">
	>({
		key: "3E7BF1735D7DFA4B",
		type: "object",
		defaultValue: {
			sumSamePartRequests: false,
			tryAppendToExistingPurchaseOrderLine: false,
		},
	});

	return (
		<AsyncForm<IsoDateString, FieldValues, void>
			fetch={() =>
				PurchaseRequestApi.getPlannedArrivalDateForPurchaseRequests({
					purchaseRequestIds: purchaseRequestIds,
				})
			}
			getDefaultValues={(plannedArrivalDate) => ({
				purchaseRequestIds: purchaseRequestIds,
				plannedArrivalDate: plannedArrivalDate,
				sumSamePartRequests: storedDefaultValues.sumSamePartRequests,
				tryAppendToExistingPurchaseOrderLine: storedDefaultValues.tryAppendToExistingPurchaseOrderLine,
			})}
			columns={1}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.purchase.purchaseOrderFromPurchaseRequests.form"),
				data: { purchaseRequestIds },
			}}
			submit={async (values) => {
				await PurchaseRequestApi.createPurchaseOrderFromSelectedPurchaseRequests(values);
			}}
			render={({ control, watch }) => (
				<>
					<FormDateField
						control={control}
						name={"plannedArrivalDate"}
						label={i18n.t("planned_arrival_date")}
						rules={requireRule()}
						disablePast
					/>
					<FormCheckbox
						control={control}
						name={"sumSamePartRequests"}
						label={i18n.t("sum_the_requests_of_the_equal_part")}
						onChange={(checked) => {
							setStoredDefaultValues((prev) => ({
								...prev,
								sumSamePartRequests: checked,
							}));
						}}
					/>
					{watch("sumSamePartRequests") && (
						<FormCheckbox
							control={control}
							name={"tryAppendToExistingPurchaseOrderLine"}
							label={i18n.t("append_to_existing_purchase_order_line")}
							onChange={(checked) => {
								setStoredDefaultValues((prev) => ({
									...prev,
									tryAppendToExistingPurchaseOrderLine: checked,
								}));
							}}
						/>
					)}
				</>
			)}
		/>
	);
};
